import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { formatLegDateLabel, formatTimeDisplay, type LegData } from './shortlistedFlightLegData'

const AirlineLogo = styled.img`
  height: ${rem(32)};
  width: ${rem(32)};
  object-fit: contain;
  flex-shrink: 0;
`

const LegRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  width: 100%;
  min-width: 0;

  ${mediaQueryUp.tablet} {
    gap: ${rem(24)};
  }
`

const LegTimes = styled(Group)`
  min-width: 0;
`

const LegAirlineName = styled(Caption)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const LegSeparator = styled.hr`
  border: none;
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  margin: 0;
  width: 100%;
`

interface Props {
  leg: LegData
}

function FlightLegRow({ leg }: Props) {
  const dateFormatter = useIntlDateFormatter()
  const departureTime = leg.departureTime ? formatTimeDisplay(leg.departureTime) : '--:--'
  const arrivalTime = leg.arrivalTime ? formatTimeDisplay(leg.arrivalTime) : '--:--'
  const dateLabel = formatLegDateLabel(leg.displayDate ?? leg.arrivalDate ?? leg.departureDate, dateFormatter)
  const routeLabel = `${leg.departureAirport ?? ''}-${leg.arrivalAirport ?? ''}`
  const flightTimeRange = `${departureTime} - ${arrivalTime}`
  return (
    <LegRow>
      {leg.carrierLogo && (
        <AirlineLogo src={leg.carrierLogo} alt={leg.airline ?? ''} />
      )}
      <LegTimes direction="vertical" gap={2}>
        {dateLabel && <BodyText variant="small" weight="bold">{dateLabel}</BodyText>}
        <Heading variant="heading6">{flightTimeRange}</Heading>
        <LegAirlineName variant="medium" colour="light-foreground-alt">{leg.airline ?? ''}</LegAirlineName>
      </LegTimes>
      <Group direction="vertical" gap={2} shrink={false}>
        <BodyText variant="medium" weight="bold">{leg.duration ?? ''}</BodyText>
        <Caption variant="medium" colour="light-foreground-alt">{routeLabel}</Caption>
      </Group>
    </LegRow>
  )
}

export default FlightLegRow
